import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { clearErrorAction, allOrderAction } from '../redux/actions/orderAction'
import swal from 'sweetalert';
import EditIcon from '@material-ui/icons/Edit'
import Sidebar from './Sidebar'
import Loader from '../layout/loader';
import './allProductByAdmin.css'


function OrdersRoutesByAdmin() {
    const dispatch = useDispatch();
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const initialStatus = searchParams.get('status') || 'All';

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(initialStatus);

    useEffect(() => {
        const queryStatus = searchParams.get('status');
        if (queryStatus) {
            setStatusFilter(queryStatus);
        }
    }, [searchParams]);

    const { error, orders, loading } = useSelector((state) => state.allOrders)

    useEffect(() => {
        if (error) {
            swal({
                title: "Validation",
                text: error,
                icon: "error",
            })
            dispatch(clearErrorAction())

        }

        dispatch(allOrderAction())

    }, [dispatch, error])

    const orderList = useMemo(() => Array.isArray(orders) ? orders : [], [orders]);

    const inrFormatter = useMemo(() => new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    }), []);

    const getPaymentLabel = (order) => {
        if (order.paymentMode === 'FULL_ONLINE') {
            return 'Paid online';
        }

        if (order.paymentMode === 'ADVANCE_COD' || Number(order.adavncePayment && order.adavncePayment.amount) > 0) {
            return 'Advance + COD';
        }

        return order.paymentInfo && order.paymentInfo.status ? order.paymentInfo.status : 'Cash on delivery';
    }

    const getPaymentTone = (order) => {
        if (order.paymentMode === 'FULL_ONLINE') {
            return 'paid';
        }

        if (order.paymentMode === 'ADVANCE_COD' || Number(order.adavncePayment && order.adavncePayment.amount) > 0) {
            return 'partial';
        }

        return 'cod';
    }

    const getStatusTone = (status) => {
        const normalizedStatus = String(status || '').toLowerCase();

        if (normalizedStatus.includes('deliver')) {
            return 'delivered';
        }

        if (normalizedStatus.includes('cancel') || normalizedStatus.includes('return')) {
            return 'danger';
        }

        if (normalizedStatus.includes('ship') || normalizedStatus.includes('confirm')) {
            return 'progress';
        }

        return 'pending';
    }

    const getOrderQuantity = (order) => {
        return (order.orderItems || []).reduce((total, item) => total + Number(item.quantity || 0), 0);
    }

    const getCustomerName = (order) => {
        return (order.shippingInfo && order.shippingInfo.name) || order.userName || 'Customer';
    }

    const getCustomerContact = (order) => {
        const email = order.shippingInfo && order.shippingInfo.email;
        const phone = order.shippingInfo && order.shippingInfo.phoneNo;
        return [email, phone].filter(Boolean).join(' | ');
    }

    const getOrderSummary = (order) => {
        const items = order.orderItems || [];
        if (!items.length) {
            return 'No items';
        }

        const firstItem = items[0];
        const extraItems = items.length - 1;
        return `${firstItem.name}${extraItems > 0 ? ` +${extraItems} more` : ''}`;
    }

    const filteredOrders = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        const normalizedFilter = String(statusFilter || 'All').toLowerCase();

        return orderList.filter((order) => {
            const statusMatch = normalizedFilter === 'all' || String(order.orderStatus || '').toLowerCase() === normalizedFilter;
            const searchableText = [
                order._id,
                getCustomerName(order),
                getCustomerContact(order),
                getOrderSummary(order),
                getPaymentLabel(order),
                order.orderStatus,
            ].join(' ').toLowerCase();

            return statusMatch && (!normalizedSearch || searchableText.includes(normalizedSearch));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderList, searchTerm, statusFilter]);

    const statusOptions = useMemo(() => {
        const uniqueStatuses = new Set(orderList.map((order) => order.orderStatus).filter(Boolean));
        if (statusFilter !== 'All' && !uniqueStatuses.has(statusFilter)) {
            uniqueStatuses.add(statusFilter);
        }
        return ['All', ...Array.from(uniqueStatuses)];
    }, [orderList, statusFilter]);

    const summary = useMemo(() => {
        return {
            total: orderList.length,
            revenue: orderList.reduce((sum, order) => sum + Number(order.totalPrice || 0), 0),
            advance: orderList.reduce((sum, order) => sum + Number((order.adavncePayment && order.adavncePayment.amount) || 0), 0),
            remaining: orderList.reduce((sum, order) => sum + Number(order.remainingAmount || ((order.adavncePayment && order.adavncePayment.remainingAmount) || 0)), 0),
        };
    }, [orderList]);

    return (
        <Fragment>
            {loading ? <Loader /> : (<Fragment>
                <div className="dashboard-container">
                    <Sidebar />
                    <div className="productListContainer admin-orders-page">
                        <div className='admin-list-hero admin-orders-hero'>
                            <div>
                                <span className="admin-hero-kicker">Order operations</span>
                                <h1>All orders</h1>
                                <p>Track customer orders, payments, delivery balance, and fulfillment status.</p>
                            </div>
                            <div className="admin-orders-hero-stats">
                                <div>
                                    <span>Total orders</span>
                                    <strong>{summary.total}</strong>
                                </div>
                                <div>
                                    <span>Order value</span>
                                    <strong>{inrFormatter.format(summary.revenue)}</strong>
                                </div>
                            </div>
                        </div>

                        <div className="admin-orders-toolbar">
                            <input
                                type="search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search order id, customer, product, payment..."
                            />
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                {statusOptions.map((status) => (
                                    <option value={status} key={status}>{status}</option>
                                ))}
                            </select>
                            <div className="admin-orders-summary-card">
                                <span>Advance paid</span>
                                <strong>{inrFormatter.format(summary.advance)}</strong>
                            </div>
                            <div className="admin-orders-summary-card">
                                <span>Balance due</span>
                                <strong>{inrFormatter.format(summary.remaining)}</strong>
                            </div>
                        </div>

                        {statusFilter !== 'All' && (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '8px 16px', background: '#fef3c7', border: '1.5px solid #fde68a', borderRadius: '10px', marginBottom: '14px', fontSize: '13px', color: '#92400e', fontWeight: 700 }}>
                                <span>Showing only <strong>{statusFilter}</strong> orders ({filteredOrders.length})</span>
                                <button
                                    type="button"
                                    onClick={() => setStatusFilter('All')}
                                    style={{ background: '#ffffff', border: '1px solid #d97706', color: '#b45309', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}
                                >
                                    ✕ Clear Filter (Show All)
                                </button>
                            </div>
                        )}

                        <div className="admin-orders-table-card">
                            <div className="admin-orders-table-scroll">
                                <table className="admin-orders-table">
                                    <thead>
                                        <tr>
                                            <th>Order</th>
                                            <th>Customer</th>
                                            <th>Items</th>
                                            <th>Payment</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredOrders.length > 0 ? filteredOrders.map((order) => {
                                            const paidAdvance = Number((order.adavncePayment && order.adavncePayment.amount) || 0);
                                            const remainingAmount = Number(order.remainingAmount || ((order.adavncePayment && order.adavncePayment.remainingAmount) || 0));

                                            return (
                                                <tr key={order._id}>
                                                    <td data-label="Order">
                                                        <div className="admin-order-id">{order._id}</div>
                                                        <span className="admin-order-date">
                                                            {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN', {
                                                                day: '2-digit',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            }) : 'No date'}
                                                        </span>
                                                    </td>
                                                    <td data-label="Customer">
                                                        <div className="admin-order-customer">{getCustomerName(order)}</div>
                                                        <span>{getCustomerContact(order) || 'No contact'}</span>
                                                        {order.shippingInfo && (
                                                            <div className="admin-order-shipping">
                                                                <strong>Ship to:</strong> {order.shippingInfo.address}, {order.shippingInfo.city}, {order.shippingInfo.district || ''} {order.shippingInfo.state} - {order.shippingInfo.pincode}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td data-label="Items">
                                                        <div className="admin-order-items-list">
                                                            {order.orderItems && order.orderItems.map((item, idx) => (
                                                                <div className="admin-order-item-row" key={idx}>
                                                                    <img src={item.image} alt={item.name} className="admin-order-item-img" />
                                                                    <div className="admin-order-item-info">
                                                                        <div className="admin-order-item-name">{item.name}</div>
                                                                        <div className="admin-order-item-meta">
                                                                            Size: {item.size} | Color: {item.colour} | Qty: {item.quantity}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <span className="admin-order-total-qty">Total: {getOrderQuantity(order)} item{getOrderQuantity(order) === 1 ? '' : 's'}</span>
                                                    </td>
                                                    <td data-label="Payment">
                                                        <span className={`admin-order-chip payment-${getPaymentTone(order)}`}>
                                                            {getPaymentLabel(order)}
                                                        </span>
                                                        <small>
                                                            Paid {inrFormatter.format(order.paymentMode === 'FULL_ONLINE' ? Number(order.totalPrice || 0) : paidAdvance)}
                                                            {remainingAmount > 0 ? ` | Due ${inrFormatter.format(remainingAmount)}` : ''}
                                                        </small>
                                                    </td>
                                                    <td data-label="Amount">
                                                        <strong className="admin-order-amount">{inrFormatter.format(Number(order.totalPrice || 0))}</strong>
                                                        {Number((order.coupanInfo && order.coupanInfo.coupanValue) || 0) > 0 && (
                                                            <span>Coupon {inrFormatter.format(order.coupanInfo.coupanValue)}</span>
                                                        )}
                                                    </td>
                                                    <td data-label="Status">
                                                        <span className={`admin-order-chip status-${getStatusTone(order.orderStatus)}`}>
                                                            {order.orderStatus || 'Processing'}
                                                        </span>
                                                    </td>
                                                    <td data-label="Action">
                                                        <Link className="admin-order-edit-button" to={`/Admin/dashboard/Edit/order/${order._id}`}>
                                                            <EditIcon /> Manage
                                                        </Link>
                                                    </td>
                                                </tr>
                                            )
                                        }) : (
                                            <tr>
                                                <td colSpan="7">
                                                    <div className="admin-orders-empty">
                                                        <strong>No orders found</strong>
                                                        <span>Try changing the search or status filter.</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                </div>

            </Fragment>)}
        </Fragment>
    )
}


export default OrdersRoutesByAdmin
